<script setup>
import {computed, watch, ref} from 'vue'
import SiteTreeNavigationMenu from "./site-tree/SiteTreeNavigationMenu.vue"
import {useSiteTreeStore} from "@/stores/siteTree"
import SiteTree from "./site-tree/SiteTree.vue"
import Button from "./Button.vue"
import PageEditorSiteTreePinBtn from "@/components/page-editor/PageEditorSiteTreePinBtn.vue"
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useAppStore} from '@/stores/appStore'
import {useRoute} from "vue-router"

const props = defineProps({
    siteTreeId: String,
    tableName: String,
    id: Number,
    pageEditor: Boolean,
    showPinBtn: {
        type: Boolean,
        default: false
    }
})

const appStore = useAppStore()
const route = useRoute()
const pageContentDataStore = usePageContentDataStore()

const siteTreeStore = useSiteTreeStore(props.siteTreeId)

const closeBtn = ref(null)

// --- Computed ---

const isSiteTreePath = computed(() => route.name === 'siteTree')
const isContentTable = computed(() => props.tableName === 'content')

const show = computed(() => {
    const isAllowedTable = props.tableName === 'pages' || props.tableName === 'content' || props.pageEditor
    const isSidebarHidden = !isSiteTreePath.value || !appStore.sidebarToggle['site_treeLeft']

    return isAllowedTable && isSidebarHidden
})

// --- Methods ---

const hide = () => {
    closeBtn.value?.click()
}

const init = () => {
    siteTreeStore.init({
        selectContentMode: isContentTable.value,
        showContent: isContentTable.value,
        onPageSelect: (pageId) => {
            const name = isSiteTreePath.value ? 'site_tree' : 'pages'
            window.location.href = `/#/${name}/${pageId}`
            hide()
        },
        onContentSelect: (pageContentId) => {
            const contentItem = pageContentDataStore.items.find(o => o.id === pageContentId)
            const contentId = contentItem?.contentId
            const name = isSiteTreePath.value ? 'site_tree' : 'content'

            window.location.href = isSiteTreePath.value
                ? `/#/${name}/c${pageContentId}`
                : `/#/${name}/${contentId}`

            hide()
        }
    })
}

const onClick = () => {
    let pageId = 0
    let pageContentLinkId = 0

    if (props.tableName === 'pages') {
        pageId = props.id
    } else if (isSiteTreePath.value) {
        // Parse 'c123' -> 123
        pageContentLinkId = parseInt(route.params.pageOrContentId.substring(1))
    }

    if (pageId) {
        siteTreeStore.setPageId(pageId)
        siteTreeStore.setExpandedPageIds(pageId)
    } else if (pageContentLinkId) {
        siteTreeStore.setContentId(pageContentLinkId)
        const pageContentLinkItem = pageContentDataStore.items.find(o => o.id === pageContentLinkId)
        if (pageContentLinkItem) {
            pageId = pageContentLinkItem.pageId
            siteTreeStore.addExpandedPageId(pageId)
            siteTreeStore.setExpandedPageIds(pageId)
        }
    }
}

// --- Watchers ---

watch(show, (isVisible) => {
    if (isVisible) {
        init()
    }
}, {immediate: true})
</script>

<template>
    <div class="d-inline">
        <Button
            v-if="show"
            class="rounded-0"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvasSiteTree"
            aria-controls="offcanvasSiteTree"
            @click="onClick"
        >
            <i class="bi-diagram-3-fill"/>
        </Button>

        <div
            id="offcanvasSiteTree"
            ref="offcanvasElement"
            class="offcanvas offcanvas-start"
            tabindex="-1"
            aria-labelledby="offcanvasSiteTreeLabel"
        >
            <div class="offcanvas-header">
                <SiteTreeNavigationMenu :site-tree-id="siteTreeId" class="w-auto"/>
                <button
                    ref="closeBtn"
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="offcanvas"
                    aria-label="Close"
                ></button>
            </div>

            <div class="offcanvas-body">
                <SiteTree :site-tree-id="siteTreeId"/>
            </div>

            <div v-if="showPinBtn" class="offcanvas-footer text-end p-3">
                <PageEditorSiteTreePinBtn/>
            </div>
        </div>
    </div>
</template>